/**
 * ============================================================================
 * UPCART V1/V2 COMPATIBLE INTEGRATION — Eureka Pet Co
 * ============================================================================
 *
 * FILE CONTENTS:
 *   PART 1: Checkout + Free Item Logic (was REPLACEMENT #1)
 *   PART 2: Cart Message / Taste Tester Logic (was REPLACEMENT #2)
 *
 * WHAT THIS HANDLES:
 *   - Free gift logic (Welcome Pack / Welcome Treat / Variety Pack)
 *   - Mobile back-button (bfcache) state reset for iOS Safari + Android Chrome
 *   - Upcart V1 (legacy) AND V2 (current) class compatibility via UPCART_SEL
 *   - V2 PRIMARY: window.upcartOnCheckout / upcartOnCartOpen / upcartOnCartClosed
 *   - V1 FALLBACK: delegated event listener on document.body
 *   - All iPhone fallback strategies (fetch → XHR → jQuery)
 *   - Shopify cart API interception for instant free-item display
 *   - Taste Tester rewards message override
 *
 * UPCART V2 MIGRATION:
 *   When Upcart support confirms V2 class names, update UPCART_SEL map below.
 *   That's the single source of truth — no other code changes needed.
 *
 * CHANGE LOG:
 *   2026-06-12 — Added Starter Pack (SKU: EURDVKB800) to Welcome Pack logic.
 *                Two lines added: one in checkAndUpdateCart(), one in
 *                determineFreeItemVariantId(). Subscription-only, same as
 *                Variety Pack pattern.
 *
 * LOAD INSTRUCTIONS (in theme.liquid):
 *   <script src="{{ 'upcart-integration.js' | asset_url }}" defer="defer"></script>
 * ============================================================================
 */

(function () {
  'use strict';

  /* ==========================================================================
   * UPCART SELECTOR MAP — UPDATE HERE ONCE WHEN V2 NAMES ARE CONFIRMED
   * Each value matches BOTH V1 (legacy) and V2 (current) class names.
   * Comma-separated selectors = OR
   * ========================================================================== */
  const UPCART_SEL = {
    cartPopup:             '#CartPopup',
    cartBody:              '.upcart-cart-body, .upcart-public-cart-body',
    productsSection:       '.upcart-products-section, .upcart-public-cart-items',
    productItem:           '.upcart-product-item, .upcart-public-component-product-tile',
    productTitle:          '.upcart-product-title, .upcart-public-component-product-tile__product-title',
    productTitleLink:      '.upcart-product-title-link, .upcart-public-component-product-tile__title-link',
    itemProperty:          '.upcart-item-property, .upcart-public-product-properties__item',
    rewards:               '.upcart-rewards, .upcart-public-rewards',
    rewardsMessage:        '.upcart-rewards-message, .upcart-public-rewards__message',
    rewardsBar:            '.upcart-rewards-bar-background, .upcart-public-component-segmented-progress-bar',
    rewardsTiersContainer: '.styles_TieredRewards__tiersContainer__ZMHzn, .upcart-public-component-segmented-progress-bar__segments-container',
    removeButton:          '.styles_TrashButton__deleteButton__8uIHq, .upcart-public-component-product-tile__remove-button',
    checkoutButton:        '.upcart-checkout-button, .upcart-public-checkout-button',
    itemBottomRow:         '.Stack.Stack--distributionEqualSpacing, .upcart-public-component-product-tile__controls'
  };

  // Expose globally so other inline scripts in theme.liquid can reference it if needed
  window.UPCART_SEL = UPCART_SEL;

  // ════════════════════════════════════════════════════════════════════════════
  // ██  PART 1: CHECKOUT + FREE ITEM LOGIC  ██
  // ════════════════════════════════════════════════════════════════════════════

  let lastCartHtml = '';

  // ── Device Detection ──────────────────────────────────────────────────────
  function isIOSDevice() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  }
  function isIPhone() {
    return /iPhone/.test(navigator.userAgent) && !window.MSStream;
  }
  function getIOSDelay(baseDelay) {
    return isIOSDevice() ? baseDelay * 2 : baseDelay;
  }
  function executeWithUserInteraction(operation) {
    if (isIPhone()) {
      return new Promise((resolve, reject) => {
        setTimeout(async () => {
          try { resolve(await operation()); }
          catch (error) { reject(error); }
        }, 0);
      });
    } else {
      return operation();
    }
  }

  // ════════════════════════════════════════════════════════════════════════════
  // CART API INTERCEPTOR
  // Fires checkAndUpdateCart() the moment any Shopify cart endpoint is hit.
  // Operates on Shopify endpoints, not Upcart DOM — version-agnostic.
  // ════════════════════════════════════════════════════════════════════════════
  (function initCartApiInterceptor() {
    const CART_API_PATTERNS = ['/cart/add', '/cart/change', '/cart/update'];
    let _pendingUpdate = null;

    function isCartApiCall(url) {
      if (!url) return false;
      const urlStr = typeof url === 'string' ? url : url.toString();
      return CART_API_PATTERNS.some(pattern => urlStr.includes(pattern));
    }

    function triggerCartDisplayUpdate() {
      if (_pendingUpdate) clearTimeout(_pendingUpdate);
      _pendingUpdate = setTimeout(() => {
        _pendingUpdate = null;
        checkAndUpdateCart();
      }, 300);
    }

    const originalFetch = window.fetch;
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : (input && input.url ? input.url : '');
      const method = (init && init.method) ? init.method.toUpperCase() : 'GET';
      if (method === 'POST' && isCartApiCall(url)) {
        return originalFetch.apply(this, arguments).then(response => {
          triggerCartDisplayUpdate();
          return response;
        });
      }
      return originalFetch.apply(this, arguments);
    };

    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this._sgMethod = method;
      this._sgUrl = url;
      return originalXHROpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      if (this._sgMethod && this._sgMethod.toUpperCase() === 'POST' && isCartApiCall(this._sgUrl)) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) triggerCartDisplayUpdate();
        });
      }
      return originalXHRSend.apply(this, arguments);
    };

    function hookJQueryAjax() {
      if (typeof $ !== 'undefined' && $.ajaxPrefilter) {
        $.ajaxPrefilter(function (options) {
          if (options.type && options.type.toUpperCase() === 'POST' && isCartApiCall(options.url)) {
            const originalSuccess = options.success;
            options.success = function () {
              if (originalSuccess) originalSuccess.apply(this, arguments);
              triggerCartDisplayUpdate();
            };
          }
        });
      }
    }

    if (typeof $ !== 'undefined') hookJQueryAjax();
    else document.addEventListener('DOMContentLoaded', hookJQueryAjax);
  })();

  // ── Cart Display Logic — uses UPCART_SEL throughout ───────────────────────
  function checkAndUpdateCart() {
    setTimeout(() => {
      fetchCartData()
        .then((cart) => {
          if (!cart) return;

          let has800gSubscription = false;
          let hasLargeSubscription = false;
          let hasTasteTester = false;
          let hasVarietyPack = false;

          cart.items.forEach((cartItem) => {
            if (cartItem.sku === 'EURDVKB-18-2' && cartItem.selling_plan_allocation) {
              hasVarietyPack = true;
            }
            // ── ADDED: Starter Pack (3x 800g) → Welcome Pack ──────────────
            if (cartItem.sku === 'EURDVKB800' && cartItem.selling_plan_allocation) {
              hasVarietyPack = true;
            }
            // ──────────────────────────────────────────────────────────────
            if (cartItem.variant_title === '800g' && cartItem.selling_plan_allocation) {
              has800gSubscription = true;
            }
            if (
              (cartItem.variant_title === '1.8kg' ||
                cartItem.variant_title === '3.6kg' ||
                cartItem.variant_title === '5.4kg') &&
              cartItem.selling_plan_allocation
            ) {
              hasLargeSubscription = true;
            }
            if (cartItem.sku == 'RFDRK000789') hasTasteTester = true;
          });

          setTimeout(() => {
            var divObject = document.querySelector(UPCART_SEL.rewards);
            var divCustMsg = document.getElementById('sg_upcart_drawer_free_ship_msg');

            if (divObject && divCustMsg) {
              if (hasTasteTester) {
                divObject.style.display = 'none';
                divCustMsg.style.display = 'block';
              } else {
                divCustMsg.style.display = 'none';
                divObject.style.display = 'block';
              }
            }

            if (hasLargeSubscription || hasVarietyPack) {
              addCartItemHTML(
                'welcome-pack-id',
                'Free Eureka Welcome Pack',
                'https://cdn.shopify.com/s/files/1/0842/3135/6715/files/extended_image005.jpg?v=1723109134',
                0
              );
              removeCartItemHTML('welcome-treat-id');
            } else {
              removeCartItemHTML('welcome-pack-id');
              if (has800gSubscription) {
                addCartItemHTML(
                  'welcome-treat-id',
                  'Free Eureka Welcome Treat',
                  'https://cdn.shopify.com/s/files/1/0842/3135/6715/files/extended_image005_book_goathorn.jpg?v=1723109069',
                  0
                );
              } else {
                removeCartItemHTML('welcome-treat-id');
              }
            }
          }, 100);
        })
        .catch(() => { /* swallow */ });
    }, 100);

    setTimeout(() => {
      const propertyTags = document.querySelectorAll(UPCART_SEL.itemProperty);
      propertyTags.forEach((tag) => {
        const text = tag.textContent || tag.innerText;
        const weeksIndex = text.indexOf('Weeks');
        if (weeksIndex !== -1) {
          const updatedText = 'ship every ' + text.substring(0, weeksIndex) + 'weeks';
          tag.textContent = updatedText;
        }
      });

      const cartItems = document.querySelectorAll(UPCART_SEL.productItem);
      cartItems.forEach((item) => {
        const titleElement = item.querySelector(UPCART_SEL.productTitle);
        if (titleElement && titleElement.textContent.includes('100% off')) {
          titleElement.textContent = titleElement.textContent
            .replace('(100% off)', '')
            .trim();
          titleElement.classList.add('upcart-bogo-title');

          const deleteButtonContainer = item.querySelector(UPCART_SEL.removeButton);
          const deleteButtonParent = deleteButtonContainer && deleteButtonContainer.parentElement;

          if (deleteButtonContainer && deleteButtonParent && !deleteButtonParent.querySelector('.upcart_free_')) {
            deleteButtonContainer.remove();
            const priceElement = document.createElement('div');
            priceElement.className = 'upcart_free_';
            priceElement.innerHTML = `
                            <span class="upcart_price">$0.00</span>
                            <div class="upcart_free">FREE</div>
                        `;
            deleteButtonParent.appendChild(priceElement);
          }

          const bottomRow = item.querySelector(UPCART_SEL.itemBottomRow);
          if (bottomRow) bottomRow.remove();
        }
      });

      setTimeout(() => {
        document.querySelectorAll(UPCART_SEL.itemProperty).forEach((div) => {
          if (div.textContent.toLowerCase().includes('bogos')) {
            div.style.setProperty('display', 'none', 'important');
          }
        });
      }, 200);
    }, 300);
  }

  // ── Cart Item HTML Helpers (V1/V2 dual-class injection) ──────────────────
  function addCartItemHTML(id, title, imageUrl, price) {
    if (document.getElementById(id)) return;

    const cartItemsSection = document.querySelector(UPCART_SEL.productsSection);
    if (!cartItemsSection) return;

    const cartItemHTML = `
            <div class="styles_ProductRow__product__AokJ5 upcart-product-item upcart-public-component-product-tile sg-injected-free-item" id="${id}">
                <div class="Stack Stack--distributionLeading Stack--spacingLoose upcart-public-component-product-tile__content">
                    <div class="Stack__item">
                        <div class="upcart-product-image-wrapper upcart-public-component-product-tile__image-wrapper styles_ProductRow__imageWrapper__CgzZq">
                            <img src="${imageUrl}" alt="${title}" class="upcart-public-component-product-tile__image">
                        </div>
                    </div>
                    <div class="Stack__item Stack__item--fill upcart-public-component-product-tile__info">
                        <div class="styles_ProductRow__productHeading__vnxnN upcart-public-component-product-tile__header">
                            <div class="styles_ProductRow__productTitleAndVariant__On413">
                                <h3 class="upcart-product-title upcart-public-component-product-tile__product-title UpcartDesignSettings__cartTextColor styles_ProductRow__productTitle__gcycG upcart_free_title">${title}</h3>
                            </div>
                            <div>
                                <div class="Stack Stack--distributionEqualSpacing Stack--alignmentCenter">
                                    <div class="Stack__item" style="margin-left: auto;">
                                        <div class="styles_ProductRow__price__Ta93X upcart-public-component-product-tile__product-pricing">
                                            <div class="upcart-item-prices-row styles_ProductRow__priceRow__BONFQ">
                                                <span class="upcart-do-not-modify-this-node-please-only-modify-child-node">
                                                    <span class="upcart-item-price upcart-public-component-product-tile__price UpcartDesignSettings__cartTextColor styles_ProductRow__ProductPrice__MuDEq upcart_free_">$${price}.00 <div class="upcart_free">FREE</div></span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    cartItemsSection.insertAdjacentHTML('beforeend', cartItemHTML);
  }

  function removeCartItemHTML(id) {
    const cartItem = document.getElementById(id);
    if (cartItem) cartItem.remove();
  }

  // ════════════════════════════════════════════════════════════════════════════
  // CHECKOUT PROCESSING GUARD (module-level — survives bfcache reset)
  // ════════════════════════════════════════════════════════════════════════════
  let _isCheckoutProcessing = false;
  let _checkoutProcessingTimer = null;

  function resetCheckoutProcessing() {
    _isCheckoutProcessing = false;
    if (_checkoutProcessingTimer) {
      clearTimeout(_checkoutProcessingTimer);
      _checkoutProcessingTimer = null;
    }
  }

  function checkAndRemoveHref() {
    const upcartCheckoutButton = document.querySelector(UPCART_SEL.checkoutButton);
    if (upcartCheckoutButton && upcartCheckoutButton.hasAttribute('href')) {
      upcartCheckoutButton.removeAttribute('href');
    }
  }

  // ════════════════════════════════════════════════════════════════════════════
  // CHECKOUT INTERCEPTION — V1 + V2 dual path
  //   V2 PRIMARY: window.upcartOnCheckout API hook (set further below)
  //   V1 FALLBACK: Delegated event listener on document.body (capture phase)
  // ════════════════════════════════════════════════════════════════════════════
  (function attachDelegatedCheckoutListener() {
    function isUpcartCheckoutButton(target) {
      if (!target || !target.matches) return null;
      try {
        if (target.matches(UPCART_SEL.checkoutButton)) return target;
        if (target.closest) return target.closest(UPCART_SEL.checkoutButton);
      } catch (e) { /* ignore selector errors */ }
      return null;
    }

    async function delegatedCheckoutHandler(event) {
      const target = isUpcartCheckoutButton(event.target);
      if (!target) return;

      if (target.hasAttribute('href')) target.removeAttribute('href');

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (_isCheckoutProcessing) return;
      _isCheckoutProcessing = true;

      try {
        await handleCheckout(event);
      } catch (error) {
        resetCheckoutProcessing();
      } finally {
        _checkoutProcessingTimer = setTimeout(resetCheckoutProcessing, 2000);
      }
    }

    document.addEventListener('click', delegatedCheckoutHandler, true);
    if (isIPhone()) {
      document.addEventListener('touchend', delegatedCheckoutHandler, true);
    }
  })();

  // ── Polling ───────────────────────────────────────────────────────────────
  function pollForCartChanges() {
    const cartItemsContainer = document.querySelector(UPCART_SEL.productsSection);
    if (cartItemsContainer) {
      const currentCartHtml = cartItemsContainer.innerHTML;
      if (currentCartHtml !== lastCartHtml) {
        lastCartHtml = currentCartHtml;
        checkAndUpdateCart();
      }
    }
    checkAndRemoveHref();

    const pollInterval = isIOSDevice() ? 200 : 100;
    setTimeout(pollForCartChanges, pollInterval);
  }

  pollForCartChanges();

  // ── Overflow ─────────────────────────────────────────────────────────────
  function removeOverflowHidden() {
    const bodyElement = document.getElementById('ep_body');
    if (bodyElement && bodyElement.classList.contains('overflow-hidden')) {
      bodyElement.classList.remove('overflow-hidden');
    }
  }

  // ════════════════════════════════════════════════════════════════════════════
  // V2 PRIMARY CHECKOUT HOOK — window.upcartOnCheckout
  // Returning false blocks Upcart's default redirect.
  // ════════════════════════════════════════════════════════════════════════════
  window.upcartOnCheckout = function () {
    if (_isCheckoutProcessing) return false;
    _isCheckoutProcessing = true;

    handleCheckout({})
      .catch(() => { resetCheckoutProcessing(); })
      .finally(() => {
        _checkoutProcessingTimer = setTimeout(resetCheckoutProcessing, 2000);
      });

    return false;
  };

  document.addEventListener('DOMContentLoaded', () => {
    if (isIPhone()) {
      const style = document.createElement('style');
      style.textContent = `
                .upcart-checkout-button,
                .upcart-public-checkout-button {
                    -webkit-touch-callout: none;
                    -webkit-user-select: none;
                    -khtml-user-select: none;
                    -moz-user-select: none;
                    -ms-user-select: none;
                    user-select: none;
                    cursor: pointer;
                }
            `;
      document.head.appendChild(style);

      const initTimes = [3000, 6000, 9000, 12000];
      initTimes.forEach((time) => { setTimeout(checkAndRemoveHref, time); });

      setTimeout(() => { if (window.upcartRefreshCart) window.upcartRefreshCart(); }, 2000);
      setTimeout(() => { if (window.upcartRefreshCart) window.upcartRefreshCart(); }, 5000);
    } else {
      setTimeout(checkAndRemoveHref, 5000);
      setTimeout(checkAndRemoveHref, 8000);
      setTimeout(checkAndRemoveHref, 10000);
      setTimeout(() => { if (window.upcartRefreshCart) window.upcartRefreshCart(); }, 3000);
    }
  });

  // ════════════════════════════════════════════════════════════════════════════
  // MOBILE BACK-BUTTON / BFCACHE HANDLING
  // Reset checkout state when iOS Safari / Android Chrome restores from bfcache
  // ════════════════════════════════════════════════════════════════════════════
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      resetCheckoutProcessing();

      const checkoutBtn = document.querySelector(UPCART_SEL.checkoutButton);
      if (checkoutBtn) {
        checkoutBtn.classList.remove('processing');
        if (checkoutBtn.innerHTML.indexOf('loading-icon') !== -1 ||
          checkoutBtn.innerHTML.indexOf('Loading') !== -1) {
          checkoutBtn.innerHTML = 'CHECKOUT';
        }
        checkoutBtn.removeAttribute('data-processing');
      }

      if (window.upcartRefreshCart) window.upcartRefreshCart();

      setTimeout(checkAndRemoveHref, 200);
      setTimeout(checkAndRemoveHref, 800);
    }
  });

  // Android Chrome backup
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') {
      resetCheckoutProcessing();

      const checkoutBtn = document.querySelector(UPCART_SEL.checkoutButton);
      if (checkoutBtn) {
        checkoutBtn.classList.remove('processing');
        if (checkoutBtn.innerHTML.indexOf('loading-icon') !== -1 ||
          checkoutBtn.innerHTML.indexOf('Loading') !== -1) {
          checkoutBtn.innerHTML = 'CHECKOUT';
        }
        checkoutBtn.removeAttribute('data-processing');
      }
    }
  });

  document.addEventListener('DOMContentLoaded', async () => {
    const cartData = await fetchCartData();
    if (!cartData) return;
    await removeExistingFreeItems();
    if (window.upcartRefreshCart) window.upcartRefreshCart();
  });


  // ════════════════════════════════════════════════════════════════════════════
  // handleCheckout — FIXED: always remove + always add (no skip logic)
  //
  // OLD BUG: skipping re-add when item already in cart caused free gift to
  // disappear on 2nd checkout click after mobile back-button.
  // FIX: always remove all existing free items, then always add correct one.
  // ════════════════════════════════════════════════════════════════════════════
  async function handleCheckout(event) {
    const upcartCheckoutButton = document.querySelector(UPCART_SEL.checkoutButton);

    let originalText = null;
    if (upcartCheckoutButton) {
      originalText = upcartCheckoutButton.innerHTML;
      if (originalText.indexOf('loading-icon') !== -1 || originalText.indexOf('Loading') !== -1) {
        originalText = 'CHECKOUT';
      }
      upcartCheckoutButton.innerHTML = '<span class="loading-icon"></span> Loading...';
      upcartCheckoutButton.classList.add('processing');
    }

    return executeWithUserInteraction(async () => {
      try {
        const cartData = await fetchCartData();
        if (!cartData) {
          if (upcartCheckoutButton && originalText !== null) {
            upcartCheckoutButton.innerHTML = originalText;
            upcartCheckoutButton.classList.remove('processing');
          }
          resetCheckoutProcessing();
          return;
        }

        const freeItemVariantId = determineFreeItemVariantId(cartData);

        const FREE_ITEM_IDS = ['46460357869852', '46322042798364'];
        const existingFreeItems = FREE_ITEM_IDS.filter(variantId =>
          cartData.items.some(item => item.variant_id.toString() === variantId)
        );

        // STEP 1: Always remove ALL existing free items (clean slate for back-button case)
        if (existingFreeItems.length > 0) {
          await Promise.all(existingFreeItems.map(id => removeItem(id)));
        }

        // STEP 2: Always add the correct free item (NO skip logic — that was the bug)
        if (freeItemVariantId) {
          await addFreeItemDirect(freeItemVariantId);
        }

        const checkoutDelay = isIPhone() ? 200 : 100;
        setTimeout(() => { redirectToCheckout(); }, checkoutDelay);

      } catch (error) {
        if (upcartCheckoutButton && originalText !== null) {
          upcartCheckoutButton.innerHTML = originalText;
          upcartCheckoutButton.classList.remove('processing');
        }
        resetCheckoutProcessing();
      }
    });
  }

  async function addFreeItemDirect(freeItemVariantId) {
    let addSuccess = false;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify({ id: freeItemVariantId, quantity: 1 }),
      });
      if (response.ok) addSuccess = true;
      else throw new Error(`Fetch response not ok: ${response.status}`);
    } catch (fetchError) { /* fall through */ }

    if (!addSuccess) {
      try {
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', '/cart/add.js', true);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.setRequestHeader('Accept', 'application/json');
          xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
          xhr.onload = function () {
            if (xhr.status >= 200 && xhr.status < 300) { addSuccess = true; resolve(); }
            else reject(new Error(`XHR failed: ${xhr.statusText}`));
          };
          xhr.onerror = () => reject(new Error('XHR network error'));
          xhr.send(JSON.stringify({ id: freeItemVariantId, quantity: 1 }));
        });
      } catch (xhrError) { /* fall through */ }
    }

    if (!addSuccess && typeof $ !== 'undefined') {
      try {
        await new Promise((resolve, reject) => {
          $.ajax({
            url: '/cart/add.js',
            type: 'POST',
            dataType: 'json',
            contentType: 'application/json',
            data: JSON.stringify({ id: freeItemVariantId, quantity: 1 }),
            success: function () { addSuccess = true; resolve(); },
            error: function (xhr, status, error) { reject(new Error(`jQuery failed: ${error}`)); },
          });
        });
      } catch (jqueryError) { /* fall through */ }
    }
  }

  function redirectToCheckout() {
    if (isIPhone()) {
      try { window.location.href = '/checkout'; }
      catch (e) {
        try {
          const link = document.createElement('a');
          link.href = '/checkout';
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (e2) {
          const form = document.createElement('form');
          form.method = 'GET';
          form.action = '/checkout';
          form.style.display = 'none';
          document.body.appendChild(form);
          form.submit();
        }
      }
    } else {
      if (document.hasFocus()) window.location.href = '/checkout';
      else setTimeout(() => { window.location.href = '/checkout'; }, 300);
    }
  }

  // ── fetchCartData (with Cache-Control: no-cache for bfcache freshness) ───
  async function fetchCartData() {
    try {
      let cartData;
      try {
        const response = await fetch('/cart.js', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Cache-Control': 'no-cache',
          },
        });
        if (!response.ok) throw new Error(`Failed to fetch cart data: ${response.statusText}`);
        cartData = await response.json();
      } catch (fetchError) {
        cartData = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', '/cart.js', true);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
          xhr.setRequestHeader('Cache-Control', 'no-cache');
          xhr.onload = function () {
            if (xhr.status >= 200 && xhr.status < 300) {
              try { resolve(JSON.parse(xhr.responseText)); }
              catch (parseError) { reject(new Error('Failed to parse cart data')); }
            } else reject(new Error(`Failed to fetch cart data: ${xhr.statusText}`));
          };
          xhr.onerror = () => reject(new Error('Network error fetching cart data'));
          xhr.send();
        });
      }
      return cartData;
    } catch (error) {
      try {
        const cartData = await new Promise((resolve, reject) => {
          $.ajax({
            url: '/cart.js',
            type: 'GET',
            dataType: 'json',
            cache: false,
            success: function (data) { resolve(data); },
            error: function (xhr, status, error) { reject(error); },
          });
        });
        return cartData;
      } catch (jqueryError) { return null; }
    }
  }

  async function isItemInCart(variantId) {
    const cartData = await fetchCartData();
    return cartData && cartData.items.some((item) => item.variant_id.toString() === variantId);
  }

  async function removeExistingFreeItems() {
    const freeItemVariantIds = ['46460357869852', '46322042798364'];
    for (const variantId of freeItemVariantIds) {
      if (await isItemInCart(variantId)) await removeItem(variantId);
    }
  }

  function determineFreeItemVariantId(cartData) {
    let freeItemVariantId = null;
    let hasHeavierSubscriptionItem = false;
    let has800gSubscriptionItem = false;
    let hasVarietyPack = false;

    cartData.items.forEach((item) => {
      if (item.sku === 'EURDVKB-18-2' && item.selling_plan_allocation) hasVarietyPack = true;
      // ── ADDED: Starter Pack (3x 800g) → Welcome Pack ──────────────────
      if (item.sku === 'EURDVKB800' && item.selling_plan_allocation) hasVarietyPack = true;
      // ──────────────────────────────────────────────────────────────────
      if (item.selling_plan_allocation) {
        const itemWeightGrams = item.grams;
        if (itemWeightGrams === 850) has800gSubscriptionItem = true;
        else if (itemWeightGrams > 850) hasHeavierSubscriptionItem = true;
      }
    });

    if (hasVarietyPack || hasHeavierSubscriptionItem) {
      freeItemVariantId = '46322042798364'; // Free Eureka Welcome Pack
    } else if (has800gSubscriptionItem) {
      freeItemVariantId = '46460357869852'; // Free Eureka Welcome Treat
    }
    return freeItemVariantId;
  }

  async function removeItem(variantId) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 0 }),
      });
      if (!response.ok) throw new Error(`Failed to remove item ${variantId} from cart: ${response.statusText}`);
    } catch (error) { /* swallow */ }
  }

  // Theme-side cart remove buttons (NOT Upcart) — reload after remove
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('cart-remove-button button').forEach((button) => {
      button.addEventListener('click', handleRemoveButtonClick);
    });
  });

  async function handleRemoveButtonClick(event) {
    const cartData = await fetchCartData();
    if (cartData) await checkAndRemoveItems(cartData);
    window.location.reload();
  }

  async function checkAndRemoveItems(cartData) {
    const variantIds = ['46681050611996', '46673711595804', '46673711628572', '46460357869852', '46322042798364'];
    for (const item of cartData.items) {
      if (variantIds.includes(item.variant_id.toString())) {
        await removeItem(item.variant_id.toString());
      }
    }
  }

  // Legacy updateCart() kept for backward compatibility — NOT used by current checkout
  async function updateCart(freeItemVariantId) {
    return executeWithUserInteraction(async () => {
      try {
        const isFreeItemInCart = await isItemInCart(freeItemVariantId);
        if (!isFreeItemInCart) await addFreeItemDirect(freeItemVariantId);
      } catch (error) { /* swallow */ }

      const refreshDelay = isIPhone() ? 1000 : 500;
      setTimeout(() => {
        if (window.upcartRefreshCart) window.upcartRefreshCart();
      }, refreshDelay);
    });
  }


  // ════════════════════════════════════════════════════════════════════════════
  // ██  PART 2: CART MESSAGE / TASTE TESTER LOGIC  ██
  // ════════════════════════════════════════════════════════════════════════════

  (function cartMessageForTasteTester() {
    var debounceTimer;
    var originalMessage = null;
    var drawerWasOpen = false;
    var firstCartAdd = true;

    // CSS — hide Upcart default rewards (both V1 and V2), fade custom one in
    var style = document.createElement('style');
    style.textContent = '' +
      '.upcart-rewards.styles_Rewards__, .upcart-public-rewards { display: none !important; }' +
      '#sg_upcart_drawer_free_ship_msg .upcart-rewards,' +
      '#sg_upcart_drawer_free_ship_msg .upcart-public-rewards { opacity: 0; transition: opacity 0.4s ease; display: block !important; }' +
      '#sg_upcart_drawer_free_ship_msg .upcart-rewards.sg-ready,' +
      '#sg_upcart_drawer_free_ship_msg .upcart-public-rewards.sg-ready { opacity: 1; }' +
      '#sg-shipping-loader { text-align: center; padding: 14px 0 18px; }' +
      '#sg-shipping-loader p { font-size: 13px; color: rgb(48,48,48); font-weight: 400; margin: 0 0 10px; letter-spacing: 0.3px; }' +
      '#sg-shipping-loader .sg-loader-bar { height: 6px; border-radius: 3px; background: rgb(235,235,235); overflow: hidden; margin: 0 20px; }' +
      '#sg-shipping-loader .sg-loader-fill { height: 100%; width: 40%; border-radius: 3px; background: rgb(250,174,80); animation: sgSlide 1.2s ease-in-out infinite; }' +
      '@keyframes sgSlide { 0% { transform: translateX(-100%); } 100% { transform: translateX(350%); } }';
    document.head.appendChild(style);

    var loaderHTML = '' +
      '<div id="sg-shipping-loader">' +
      '  <p>Calculating Shipping...</p>' +
      '  <div class="sg-loader-bar"><div class="sg-loader-fill"></div></div>' +
      '</div>';

    function showLoading() {
      var customContainer = document.getElementById('sg_upcart_drawer_free_ship_msg');
      if (!customContainer) return;
      var reward = customContainer.querySelector(UPCART_SEL.rewards);
      if (reward) reward.classList.remove('sg-ready');
      if (!document.getElementById('sg-shipping-loader')) {
        customContainer.insertAdjacentHTML('afterbegin', loaderHTML);
      }
    }

    function hideLoading() {
      var loader = document.getElementById('sg-shipping-loader');
      if (loader) loader.remove();
      var customContainer = document.getElementById('sg_upcart_drawer_free_ship_msg');
      if (customContainer) {
        var reward = customContainer.querySelector(UPCART_SEL.rewards);
        if (reward) reward.classList.add('sg-ready');
      }
    }

    function showLoadingWithRetry() {
      var retryCount = 0;
      var retryInterval = setInterval(function () {
        retryCount++;
        var container = document.getElementById('sg_upcart_drawer_free_ship_msg');
        if (container) {
          clearInterval(retryInterval);
          showLoading();
          waitForProductsThenReveal();
        }
        if (retryCount > 50) clearInterval(retryInterval);
      }, 100);
    }

    function waitForProductsThenReveal() {
      var checkCount = 0;
      var checker = setInterval(function () {
        checkCount++;
        var items = document.querySelectorAll(UPCART_SEL.productItem);
        if (items.length > 0 || checkCount > 15) {
          clearInterval(checker);
          setTimeout(function () {
            updateRewardsMessage();
            hideLoading();
          }, 300);
        }
      }, 200);
    }

    function updateRewardsMessage() {
      var customContainer = document.getElementById('sg_upcart_drawer_free_ship_msg');
      if (!customContainer) return;

      var rewardsMessage = customContainer.querySelector(UPCART_SEL.rewardsMessage);
      if (!rewardsMessage) return;

      if (originalMessage === null && rewardsMessage.innerHTML.indexOf('Your sample is on its way') === -1) {
        originalMessage = rewardsMessage.innerHTML;
      }

      var cartItems = document.querySelectorAll(UPCART_SEL.productItem);
      var rewardsBar = customContainer.querySelector(UPCART_SEL.rewardsBar);
      var tiersContainer = customContainer.querySelector(UPCART_SEL.rewardsTiersContainer);

      var isSampleOnly = false;

      if (cartItems.length === 1) {
        var titleEl = cartItems[0].querySelector(UPCART_SEL.productTitleLink) ||
          cartItems[0].querySelector(UPCART_SEL.productTitle);
        if (titleEl && titleEl.textContent.trim() === 'Taste Tester') {
          isSampleOnly = true;
        }
      }

      if (isSampleOnly) {
        rewardsMessage.innerHTML = '<p style="font-weight: 300;"><strong>Your sample is on its way!</strong></p>';
        if (rewardsBar) rewardsBar.style.display = 'none';
        if (tiersContainer) tiersContainer.style.display = 'none';
      } else {
        if (originalMessage && rewardsMessage.innerHTML.indexOf('Your sample is on its way') !== -1) {
          rewardsMessage.innerHTML = originalMessage;
        }
        if (rewardsBar) rewardsBar.style.display = '';
        if (tiersContainer) tiersContainer.style.display = '';
      }
    }

    function debouncedUpdate() {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(updateRewardsMessage, 300);
    }

    var observersAttached = false;
    function attachObservers() {
      if (observersAttached) return;
      var cartBody = document.querySelector(UPCART_SEL.cartBody);
      if (cartBody) {
        observersAttached = true;
        new MutationObserver(debouncedUpdate).observe(cartBody, { childList: true, subtree: true });
      }
    }

    // ────────────────────────────────────────────────────────────────────────
    // Cart drawer open/close detection — V1/V2 COMPATIBLE
    //   PRIMARY:  window.upcartOnCartOpen / upcartOnCartClosed API hooks
    //   FALLBACK: visibility-based detection (computed style + bounding rect)
    // ────────────────────────────────────────────────────────────────────────
    function onDrawerOpen() {
      if (!drawerWasOpen) {
        drawerWasOpen = true;
        showLoading();
        attachObservers();
        waitForProductsThenReveal();
      }
    }

    function onDrawerClose() {
      if (drawerWasOpen) {
        drawerWasOpen = false;
        var loader = document.getElementById('sg-shipping-loader');
        if (loader) loader.remove();
        var customContainer = document.getElementById('sg_upcart_drawer_free_ship_msg');
        if (customContainer) {
          var reward = customContainer.querySelector(UPCART_SEL.rewards);
          if (reward) reward.classList.remove('sg-ready');
        }
        // Also reset overflow when drawer closes
        removeOverflowHidden();
      }
    }

    // PRIMARY: documented Upcart API hooks (preserve any existing handlers)
    var existingOnClosed = window.upcartOnCartClosed;
    window.upcartOnCartClosed = function () {
      if (typeof existingOnClosed === 'function') {
        try { existingOnClosed.apply(this, arguments); } catch (e) { }
      }
      onDrawerClose();
    };

    var existingOnOpen = window.upcartOnCartOpen;
    window.upcartOnCartOpen = function () {
      if (typeof existingOnOpen === 'function') {
        try { existingOnOpen.apply(this, arguments); } catch (e) { }
      }
      onDrawerOpen();
    };

    // FALLBACK: visibility observer on #CartPopup
    function watchCartDrawer() {
      var cartPopup = document.querySelector(UPCART_SEL.cartPopup);
      if (!cartPopup) return;

      function isVisible(el) {
        if (!el) return false;
        var cs = window.getComputedStyle(el);
        if (cs.display === 'none' || cs.visibility === 'hidden') return false;
        var rect = el.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) return false;
        return true;
      }

      new MutationObserver(function () {
        var isOpen = isVisible(cartPopup);
        if (isOpen && !drawerWasOpen) onDrawerOpen();
        if (!isOpen && drawerWasOpen) onDrawerClose();
      }).observe(cartPopup, {
        attributes: true,
        attributeFilter: ['class', 'style', 'aria-hidden', 'data-state', 'hidden']
      });
    }

    // Intercept Shopify cart endpoints to trigger rewards updates
    // Note: Part 1 also wraps window.fetch — we wrap it again here. Order doesn't
    // matter because both wrappers preserve the original via closure.
    var originalFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      var result = originalFetch.apply(this, arguments);
      if (typeof url === 'string' && (url.indexOf('/cart/add') !== -1 || url.indexOf('/cart/change') !== -1 || url.indexOf('/cart/update') !== -1 || url.indexOf('/cart/clear') !== -1)) {
        if (firstCartAdd && url.indexOf('/cart/add') !== -1) {
          firstCartAdd = false;
          showLoadingWithRetry();
        }
        if (result && typeof result.then === 'function') {
          result.then(function () {
            attachObservers();
            setTimeout(updateRewardsMessage, 800);
            setTimeout(updateRewardsMessage, 1500);
          });
        }
      }
      return result;
    };

    // Poll briefly for CartPopup to appear, then attach watcher
    var poll = setInterval(function () {
      var cartPopup = document.querySelector(UPCART_SEL.cartPopup);
      if (cartPopup) {
        clearInterval(poll);
        var cs = window.getComputedStyle(cartPopup);
        var rect = cartPopup.getBoundingClientRect();
        drawerWasOpen = cs.display !== 'none' && cs.visibility !== 'hidden' && (rect.width > 0 || rect.height > 0);
        watchCartDrawer();
      }
    }, 200);

    setTimeout(function () { clearInterval(poll); }, 10000);

    setTimeout(function () {
      updateRewardsMessage();
      attachObservers();
      setTimeout(updateRewardsMessage, 500);
      setTimeout(updateRewardsMessage, 1500);
    }, 1000);
  })();

})();